import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { KeyRound, Mail, ShieldCheck, ShieldOff, Trash2, Building2 } from "lucide-react";

// Mirror of backend PERMISSION_KEYS, displayed in tidy groups with view/create/edit/delete columns.
const PERMISSION_GROUPS = [
  {
    label: "Students",
    items: [
      { key: "students_view", label: "View" },
      { key: "students_create", label: "Create" },
      { key: "students_edit", label: "Edit" },
      { key: "students_delete", label: "Delete" },
    ],
  },
  {
    label: "Student Accounts",
    items: [
      { key: "student_accounts_view", label: "View" },
      { key: "student_accounts_create", label: "Create" },
      { key: "student_accounts_edit", label: "Edit / Pay" },
      { key: "student_accounts_delete", label: "Delete" },
    ],
  },
  {
    label: "Statement of Account",
    items: [
      { key: "statement_of_account_view", label: "View" },
      { key: "statement_of_account_print", label: "Print" },
    ],
  },
  {
    label: "Grading Setup",
    items: [
      { key: "grading_setup_view", label: "View" },
      { key: "grading_setup_create", label: "Create" },
      { key: "grading_setup_edit", label: "Edit" },
      { key: "grading_setup_delete", label: "Delete" },
    ],
  },
  {
    label: "Grades",
    items: [
      { key: "grades_view", label: "View" },
      { key: "grades_create", label: "Create" },
      { key: "grades_edit", label: "Edit" },
      { key: "grades_delete", label: "Delete" },
    ],
  },
  {
    label: "Report Cards",
    items: [
      { key: "report_cards_view", label: "View" },
      { key: "report_cards_create", label: "Create" },
      { key: "report_cards_edit", label: "Edit" },
      { key: "report_cards_delete", label: "Delete" },
    ],
  },
  {
    label: "Progress Feedback",
    items: [
      { key: "progress_feedback_view", label: "View" },
      { key: "progress_feedback_create", label: "Create / Submit" },
      { key: "progress_feedback_edit", label: "Edit / Approve / Reject" },
      { key: "progress_feedback_delete", label: "Delete" },
    ],
  },
  {
    label: "Employees",
    items: [
      { key: "employees_view", label: "View" },
      { key: "employees_create", label: "Create" },
      { key: "employees_edit", label: "Edit" },
      { key: "employees_delete", label: "Delete" },
    ],
  },
  {
    label: "Employee Other Deductions",
    items: [
      { key: "employee_deductions_view", label: "View" },
      { key: "employee_deductions_create", label: "Create" },
      { key: "employee_deductions_edit", label: "Edit" },
      { key: "employee_deductions_delete", label: "Delete" },
    ],
  },
  {
    label: "Employee Attendance",
    items: [
      { key: "employee_attendance_view", label: "View" },
      { key: "employee_attendance_create", label: "Create" },
      { key: "employee_attendance_edit", label: "Edit" },
      { key: "employee_attendance_delete", label: "Delete" },
    ],
  },
  {
    label: "Employee Salary Module",
    items: [
      { key: "salary_view", label: "View" },
      { key: "salary_create", label: "Create" },
      { key: "salary_edit", label: "Edit" },
      { key: "salary_delete", label: "Delete" },
    ],
  },
  {
    label: "Employee Salary Formula",
    items: [
      { key: "salary_formula_view", label: "View" },
      { key: "salary_formula_create", label: "Create" },
      { key: "salary_formula_edit", label: "Edit" },
      { key: "salary_formula_delete", label: "Delete" },
    ],
  },
  {
    label: "Pay Slip",
    items: [
      { key: "payslip_view", label: "View" },
      { key: "payslip_create", label: "Create / Mark Paid" },
      { key: "payslip_edit", label: "Edit / Adjust" },
      { key: "payslip_delete", label: "Delete" },
    ],
  },
  {
    label: "Pay Slip Overview",
    items: [
      { key: "payslip_overview_view", label: "View" },
      { key: "payslip_overview_create", label: "Save snapshot" },
      { key: "payslip_overview_edit", label: "Edit" },
      { key: "payslip_overview_delete", label: "Delete" },
    ],
  },
  {
    label: "Pay Slip History",
    items: [
      { key: "payslip_history_view", label: "View" },
      { key: "payslip_history_create", label: "Create" },
      { key: "payslip_history_edit", label: "Edit" },
      { key: "payslip_history_delete", label: "Delete" },
    ],
  },
  {
    label: "Classes & Sections",
    items: [
      { key: "classes_view", label: "View" },
      { key: "classes_create", label: "Create" },
      { key: "classes_edit", label: "Edit" },
      { key: "classes_delete", label: "Delete" },
    ],
  },
  {
    label: "Supplies & Inventory",
    items: [
      { key: "inventory_view", label: "View" },
      { key: "inventory_create", label: "Create" },
      { key: "inventory_edit", label: "Edit" },
      { key: "inventory_delete", label: "Delete" },
      { key: "inventory_add_stock", label: "Add stock (replenishment)" },
      { key: "inventory_deduct_stock", label: "Deduct stock (issuance/loss)" },
    ],
  },
  {
    label: "Events",
    items: [
      { key: "events_view", label: "View" },
      { key: "events_create", label: "Create" },
      { key: "events_edit", label: "Edit" },
      { key: "events_delete", label: "Delete" },
    ],
  },
  {
    label: "Parent Portal",
    items: [
      { key: "parent_portal_view", label: "View settings" },
      { key: "parent_portal_manage", label: "Manage visibility & toggles" },
    ],
  },
  {
    label: "Tuition",
    items: [
      { key: "tuition_view", label: "View plans" },
      { key: "tuition_record_payment", label: "Record payment" },
      { key: "tuition_edit_payment", label: "Edit payment" },
      { key: "tuition_delete_payment", label: "Delete payment" },
      { key: "tuition_manage_plan", label: "Manage plan (create/edit/delete)" },
    ],
  },
  {
    label: "Attendance",
    items: [{ key: "attendance_mark", label: "Mark attendance (class + individual)" }],
  },
  {
    label: "Accounting · Income",
    items: [
      { key: "accounting_income_view", label: "View" },
      { key: "accounting_income_create", label: "Create" },
      { key: "accounting_income_edit", label: "Edit" },
      { key: "accounting_income_delete", label: "Delete" },
    ],
  },
  {
    label: "Accounting · Expenses",
    items: [
      { key: "accounting_expenses_view", label: "View" },
      { key: "accounting_expenses_create", label: "Create" },
      { key: "accounting_expenses_edit", label: "Edit" },
      { key: "accounting_expenses_delete", label: "Delete" },
    ],
  },
  {
    label: "Cash Analysis",
    items: [
      { key: "cash_analysis_view", label: "View" },
      { key: "cash_analysis_manage", label: "Manage (add/delete entries + close month)" },
    ],
  },
  {
    label: "Reports",
    items: [{ key: "reports_view", label: "View accounting reports" }],
  },
  {
    label: "Branch Documents",
    items: [
      { key: "branch_documents_view", label: "View documents" },
      { key: "branch_documents_manage", label: "Manage (add/edit/delete)" },
    ],
  },
  {
    label: "Program Assessment",
    items: [
      { key: "program_assessment_view", label: "View payments" },
      { key: "program_assessment_create", label: "Record new payment" },
      { key: "program_assessment_delete", label: "Delete a payment" },
    ],
  },
  {
    label: "Retention Fee",
    items: [
      { key: "retention_fee_view", label: "View payments" },
      { key: "retention_fee_create", label: "Record new payment" },
      { key: "retention_fee_deduct", label: "Deduct on absent session" },
      { key: "retention_fee_delete", label: "Delete a payment" },
    ],
  },
  {
    label: "Individual Sessions",
    items: [
      { key: "session_forfeit", label: "Forfeit a paid session" },
    ],
  },
  {
    label: "Inquiries",
    items: [
      { key: "inquiries_view", label: "View inquiries" },
      { key: "inquiries_manage", label: "Manage (status / notes / delete)" },
    ],
  },
  {
    label: "KPI Targets",
    items: [
      { key: "kpis_create", label: "Create KPIs" },
      { key: "kpis_edit", label: "Edit KPIs & progress entries" },
      { key: "kpis_delete", label: "Delete KPIs & progress entries" },
    ],
  },
];

const ALL_PERMS = PERMISSION_GROUPS.flatMap((g) => g.items.map((i) => i.key));
const emptyPerms = () => Object.fromEntries(ALL_PERMS.map((k) => [k, false]));

export default function StaffAccessDialog({ open, onOpenChange, employee, branches }) {
  // branches: full list of approved branches under this owner's school(s)
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [access, setAccess] = useState(null); // existing staff access or null
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [branchIds, setBranchIds] = useState([]);
  const [perms, setPerms] = useState(emptyPerms());
  const [confirmRevoke, setConfirmRevoke] = useState(false);

  const branchesForEmployee = useMemo(
    () => branches.filter((b) => b.status === "approved" && b.school_id === employee?.school_id),
    [branches, employee?.school_id]
  );

  // Reset on open
  useEffect(() => {
    if (!open || !employee) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/employees/${employee.id}/access`);
        if (cancelled) return;
        if (data) {
          setAccess(data);
          setEmail(data.email);
          setPassword("");
          setBranchIds(data.branch_ids || []);
          setPerms({ ...emptyPerms(), ...(data.permissions || {}) });
        } else {
          setAccess(null);
          setEmail(employee.email || "");
          setPassword("");
          setBranchIds([employee.branch_id].filter(Boolean));
          setPerms(emptyPerms());
        }
      } catch (e) {
        if (!cancelled) toast.error(formatApiError(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, employee]);

  const togglePerm = (key) => setPerms((p) => ({ ...p, [key]: !p[key] }));
  const toggleBranch = (id) =>
    setBranchIds((arr) => (arr.includes(id) ? arr.filter((b) => b !== id) : [...arr, id]));

  const grant = async () => {
    if (!email || (!access && !password)) {
      toast.error("Email and password are required");
      return;
    }
    if (!branchIds.includes(employee.branch_id)) {
      toast.error("Employee's home branch must be assigned");
      return;
    }
    setBusy(true);
    try {
      if (access) {
        const payload = {
          branch_ids: branchIds,
          permissions: perms,
          is_active: access.is_active,
        };
        if (password) payload.password = password;
        const { data } = await api.patch(`/employees/${employee.id}/access`, payload);
        setAccess(data);
        toast.success("Staff access updated");
      } else {
        const { data } = await api.post(`/employees/${employee.id}/access`, {
          email,
          password,
          branch_ids: branchIds,
          permissions: perms,
        });
        setAccess(data);
        toast.success(`${employee.name} can now log in with ${email}`);
      }
      setPassword("");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async () => {
    if (!access) return;
    setBusy(true);
    try {
      const { data } = await api.patch(`/employees/${employee.id}/access`, {
        is_active: !access.is_active,
      });
      setAccess(data);
      toast.success(data.is_active ? "Access enabled" : "Access disabled");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const revoke = async () => {
    setBusy(true);
    try {
      await api.delete(`/employees/${employee.id}/access`);
      toast.success("Staff access revoked");
      setConfirmRevoke(false);
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          className="rounded-3xl border-brand-purple/20 max-w-2xl max-h-[90vh] overflow-y-auto"
          data-testid="staff-access-dialog"
        >
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-brand-purple" />
              Staff Access · {employee?.name}
            </DialogTitle>
            <DialogDescription>
              Grant this employee a login so they can record payments, mark attendance, or view students for the branches you select.
            </DialogDescription>
          </DialogHeader>

          {loading ? (
            <div className="py-10 text-center text-brand-inkMuted font-heading">Loading...</div>
          ) : (
            <div className="space-y-5">
              {access && (
                <div className="rounded-2xl border border-brand-purple/15 bg-[#F1F5FF] px-4 py-3 flex items-center justify-between flex-wrap gap-2" data-testid="staff-access-status">
                  <div className="flex items-center gap-2 text-sm">
                    <ShieldCheck className={`w-4 h-4 ${access.is_active ? "text-[#2E7D32]" : "text-brand-inkMuted"}`} />
                    <span className="font-semibold text-foreground">{access.email}</span>
                    <span className="text-brand-inkMuted">
                      · {access.is_active ? "Active" : "Disabled"}
                    </span>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={toggleActive}
                    disabled={busy}
                    className="rounded-full border-brand-purple/30 text-sm"
                    data-testid="staff-access-toggle-active"
                  >
                    {access.is_active ? (
                      <><ShieldOff className="w-3.5 h-3.5 mr-1.5" /> Disable</>
                    ) : (
                      <><ShieldCheck className="w-3.5 h-3.5 mr-1.5" /> Enable</>
                    )}
                  </Button>
                </div>
              )}

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="font-heading">
                    Email <span className="text-[#C62828]">*</span>
                  </Label>
                  <div className="mt-1.5 relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-inkMuted" />
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      disabled={!!access}
                      placeholder="staff@school.com"
                      className="rounded-2xl h-11 pl-9 border-brand-purple/20 disabled:bg-brand-purpleLight/30"
                      data-testid="staff-access-email"
                    />
                  </div>
                </div>

                <div>
                  <Label className="font-heading">
                    {access ? "New password (optional)" : <>Password <span className="text-[#C62828]">*</span></>}
                  </Label>
                  <Input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="At least 6 characters"
                    minLength={6}
                    className="rounded-2xl h-11 border-brand-purple/20 mt-1.5"
                    data-testid="staff-access-password"
                  />
                </div>
              </div>

              <div>
                <Label className="font-heading flex items-center gap-1.5">
                  <Building2 className="w-3.5 h-3.5" /> Assigned branches
                </Label>
                <p className="text-xs text-brand-inkMuted mt-0.5">
                  Staff can access data only for branches listed here. The employee's home branch is always included.
                </p>
                <div className="grid sm:grid-cols-2 gap-2 mt-2.5">
                  {branchesForEmployee.length === 0 && (
                    <div className="text-sm text-brand-inkMuted col-span-2">No approved branches under this school yet.</div>
                  )}
                  {branchesForEmployee.map((b) => {
                    const checked = branchIds.includes(b.id);
                    const isHome = b.id === employee?.branch_id;
                    return (
                      <label
                        key={b.id}
                        className={`flex items-center gap-2 rounded-2xl border px-3 py-2 text-sm cursor-pointer transition-colors ${
                          checked
                            ? "border-brand-purple bg-brand-purpleLight/40"
                            : "border-brand-purple/15 hover:bg-[#FAFBFE]"
                        }`}
                        data-testid={`staff-branch-toggle-${b.id}`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleBranch(b.id)}
                          disabled={isHome}
                          className="w-4 h-4 accent-brand-purple"
                        />
                        <span className="flex-1 truncate">{b.name}</span>
                        {isHome && <span className="text-[10px] uppercase tracking-wider text-brand-purple font-semibold">Home</span>}
                      </label>
                    );
                  })}
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="font-heading">Permissions</Label>
                  <div className="flex gap-1.5">
                    <button
                      type="button"
                      onClick={() => setPerms(Object.fromEntries(ALL_PERMS.map((k) => [k, true])))}
                      className="text-[11px] font-semibold uppercase tracking-wider text-brand-purpleHover hover:text-brand-purple"
                      data-testid="staff-perms-all"
                    >
                      Grant all
                    </button>
                    <span className="text-brand-inkMuted text-xs">·</span>
                    <button
                      type="button"
                      onClick={() => setPerms(emptyPerms())}
                      className="text-[11px] font-semibold uppercase tracking-wider text-brand-inkMuted hover:text-foreground"
                      data-testid="staff-perms-none"
                    >
                      Clear
                    </button>
                  </div>
                </div>
                <div className="space-y-3">
                  {PERMISSION_GROUPS.map((g) => {
                    const groupKeys = g.items.map((i) => i.key);
                    const all = groupKeys.every((k) => perms[k]);
                    const some = !all && groupKeys.some((k) => perms[k]);
                    return (
                      <div key={g.label} className="rounded-2xl border border-brand-purple/15 p-3">
                        <div className="flex items-center justify-between mb-2">
                          <p className="font-heading font-semibold text-sm text-foreground">{g.label}</p>
                          <button
                            type="button"
                            onClick={() => {
                              const next = { ...perms };
                              const target = !all;
                              groupKeys.forEach((k) => { next[k] = target; });
                              setPerms(next);
                            }}
                            className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full ${
                              all ? "bg-brand-purple/15 text-brand-purpleHover"
                                  : some ? "bg-[#FFF3E0] text-[#E65100]"
                                  : "bg-[#F1F5FF] text-brand-inkMuted hover:text-brand-purple"
                            }`}
                            data-testid={`staff-group-toggle-${g.label.replace(/\W+/g, "-").toLowerCase()}`}
                          >
                            {all ? "All on" : some ? "Some on" : "All off"}
                          </button>
                        </div>
                        <div className="grid sm:grid-cols-2 gap-1.5">
                          {g.items.map((p) => (
                            <label
                              key={p.key}
                              className="flex items-center gap-2 rounded-xl px-2 py-1.5 text-sm cursor-pointer hover:bg-[#FAFBFE]"
                              data-testid={`staff-perm-toggle-${p.key}`}
                            >
                              <input
                                type="checkbox"
                                checked={!!perms[p.key]}
                                onChange={() => togglePerm(p.key)}
                                className="w-4 h-4 accent-brand-purple"
                              />
                              <span>{p.label}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="mt-2 flex-wrap gap-2 justify-between sm:justify-between">
            <div>
              {access && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setConfirmRevoke(true)}
                  disabled={busy}
                  className="rounded-full border-[#C62828]/40 text-[#C62828] hover:bg-[#FFEBEE]"
                  data-testid="staff-access-revoke-btn"
                >
                  <Trash2 className="w-4 h-4 mr-1.5" /> Revoke Access
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="rounded-full border-brand-purple/30">
                Close
              </Button>
              <Button
                type="button"
                onClick={grant}
                disabled={busy || loading}
                className="rounded-full bg-brand-purple hover:bg-brand-purpleHover text-white"
                data-testid="staff-access-save-btn"
              >
                {busy ? "Saving..." : access ? "Save Changes" : "Grant Access"}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmRevoke} onOpenChange={setConfirmRevoke}>
        <AlertDialogContent className="rounded-3xl border-brand-purple/20" data-testid="staff-access-revoke-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-2xl">Revoke {employee?.name}'s access?</AlertDialogTitle>
            <AlertDialogDescription>
              This deletes their login immediately. They will no longer be able to sign in. The employee record itself stays intact.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full border-brand-purple/30">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={revoke}
              className="rounded-full bg-[#C62828] hover:bg-[#B71C1C] text-white"
              data-testid="confirm-staff-access-revoke"
            >
              Revoke
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
